import { useState } from 'react';
import Button from '../../../components/base/Button';
import Reveal from './Reveal';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    projectType: '',
    budget: '',
    message: '',
    timeline: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      projectType: '',
      budget: '',
      message: '',
      timeline: ''
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // 実際のフォーム要素から値を取得（オートフィルにも対応）
    const nativeForm = new FormData(e.currentTarget);

    // 迷惑送信対策フィールド。入力があれば送信せず、通常と同じ反応を返す
    const honeypot = String(nativeForm.get('website_alt') ?? '').trim();
    if (honeypot !== '') {
      setSubmitStatus('success');
      resetForm();
      return;
    }
    nativeForm.delete('website_alt');

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setFormError('');

    try {
      const body = new URLSearchParams();
      nativeForm.forEach((value, key) => {
        body.append(key, String(value));
      });

      const response = await fetch('/contact-handler.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMessage = (parsed?.meta?.message ?? parsed?.meta?.detail ?? responseText ?? '').toString();
      const isSpam = serverMessage.toLowerCase().includes('spam');
      const isSuccess = response.ok && parsed?.code === 'OK' && !isSpam;

      if (isSuccess) {
        setSubmitStatus('success');
        resetForm();
      } else {
        setSubmitStatus('error');
        setFormError(serverMessage || '送信に失敗しました。お手数ですが、再度お試しください。');
      }
    } catch (error) {
      setSubmitStatus('error');
      setFormError('通信エラーが発生しました。時間をおいて再度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  const workflowSteps = [
    {
      number: 1,
      title: 'お問い合わせ',
      description: 'メールまたは電話でご連絡ください',
      icon: 'ri-mail-send-line',
    },
    {
      number: 2,
      title: '打合せ・ヒアリング',
      description: 'ご要望を詳しくお伺いします',
      icon: 'ri-discuss-line',
    },
    {
      number: 3,
      title: 'ご提案・お見積り',
      description: '最適なプランをご提案します',
      icon: 'ri-file-text-line',
    },
    {
      number: 4,
      title: '制作・編集',
      description: '音響制作を開始します',
      icon: 'ri-music-2-line',
    },
    {
      number: 5,
      title: '試聴・修正',
      description: 'ご確認いただき調整します',
      icon: 'ri-headphone-line',
    },
    {
      number: 6,
      title: '納品',
      description: '完成した音源をお届けします',
      icon: 'ri-checkbox-circle-line',
    }
  ];

  const inputClass =
    'w-full px-3 py-2 border border-white/15 bg-white/5 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400 text-white placeholder-zinc-500 text-sm';
  const labelClass = 'block text-sm font-medium text-zinc-300 mb-2';

  return (
    <section id="contact" className="py-24 md:py-32 bg-site-mid">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <Reveal>
          <div className="text-center mb-20">
            <p className="eyebrow text-teal-400 text-sm md:text-base mb-4">Contact</p>
            <h2 className="text-3xl md:text-5xl font-mincho text-white mb-6">お問い合わせ</h2>
            <div className="w-20 h-[3px] bg-teal-400 mx-auto mb-6 rounded-full"></div>
            <p className="text-xl text-zinc-400 text-pretty">
              どんな些細なことでもお気軽にお問い合わせください。<br />
              メールでご連絡いただければ、こちらから折り返しいたします。
            </p>
          </div>
          </Reveal>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="space-y-6">
              <div className="bg-white/[0.03] border border-white/10 p-6 rounded-xl">
                <h3 className="text-xl font-mincho text-white mb-4">連絡先情報</h3>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <i className="ri-mail-line text-teal-400 text-xl mr-3"></i>
                    <div>
                      <p className="font-medium text-white">メール</p>
                      <p className="text-zinc-400">waku-w.company@waku-waku.biz</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-phone-line text-teal-400 text-xl mr-3"></i>
                    <div>
                      <p className="font-medium text-white">電話</p>
                      <p className="text-zinc-400">090-3672-6149</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-map-pin-line text-teal-400 text-xl mr-3"></i>
                    <div>
                      <p className="font-medium text-white">住所</p>
                      <p className="text-zinc-400">大阪府箕面市小野原東2丁目3番22号</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-teal-600 to-teal-500 text-white p-6 rounded-xl">
                <h3 className="text-2xl font-mincho mb-6 flex items-center">
                  <i className="ri-route-line mr-3 text-3xl"></i>
                  ご依頼の流れ
                </h3>
                <div className="space-y-4">
                  {workflowSteps.map((step, index) => (
                    <div key={index} className="bg-black/15 rounded-lg p-4 hover:bg-black/25 transition-all">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <div className="bg-white text-teal-600 rounded-full w-10 h-10 flex items-center justify-center text-lg font-bold">
                            {step.number}
                          </div>
                        </div>
                        <div className="ml-4 flex-1">
                          <div className="flex items-center mb-1">
                            <i className={`${step.icon} text-xl mr-2`}></i>
                            <h4 className="font-medium text-lg">{step.title}</h4>
                          </div>
                          <p className="text-white/90 text-sm">{step.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-6 pt-6 border-t border-white/20">
                  <p className="text-sm text-white/80 text-center">
                    <i className="ri-time-line mr-2"></i>
                    通常、お問い合わせから納品まで2〜4週間程度です
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white/[0.03] border border-white/10 p-8 rounded-xl">
              <h3 className="text-xl font-mincho text-white mb-6">お問い合わせフォーム</h3>

              {submitStatus === 'success' && (
                <div role="status" aria-live="polite" className="bg-teal-500/10 border border-teal-400/30 text-teal-200 p-4 rounded-lg mb-6">
                  <i className="ri-checkbox-circle-line mr-2" aria-hidden="true"></i>
                  お問い合わせありがとうございます。内容を確認次第、ご連絡いたします。
                </div>
              )}

              {submitStatus === 'error' && (
                <div role="alert" className="bg-red-500/10 border border-red-400/30 text-red-200 p-4 rounded-lg mb-6">
                  <i className="ri-error-warning-line mr-2" aria-hidden="true"></i>
                  {formError}
                </div>
              )}

              <form id="contact-form" onSubmit={handleSubmit} className="space-y-4">
                {/* 迷惑送信対策（通常の利用者には表示されません） */}
                <div className="assist-field-label" aria-hidden="true">
                  <label htmlFor="contact-website-alt">ウェブサイト（入力しないでください）</label>
                  <input
                    id="contact-website-alt"
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    readOnly
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contact-name" className={labelClass}>
                      お名前 <span className="text-red-400">*</span>
                    </label>
                    <input
                      id="contact-name"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      autoComplete="name"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className={labelClass}>
                      メールアドレス <span className="text-red-400">*</span>
                    </label>
                    <input
                      id="contact-email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      autoComplete="email"
                      className={inputClass}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contact-phone" className={labelClass}>電話番号</label>
                    <input
                      id="contact-phone"
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      autoComplete="tel"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-company" className={labelClass}>会社名・団体名</label>
                    <input
                      id="contact-company"
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      autoComplete="organization"
                      className={inputClass}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contact-project-type" className={labelClass}>プロジェクトの種類</label>
                    <div className="relative">
                      <select
                        id="contact-project-type"
                        name="projectType"
                        value={formData.projectType}
                        onChange={handleInputChange}
                        className={`${inputClass} pr-8 appearance-none`}
                      >
                        <option value="">選択してください</option>
                        <option value="企業VP・CM">企業VP・CM</option>
                        <option value="映画・映像MA">映画・映像MA</option>
                        <option value="展示会・イベント">展示会・イベント</option>
                        <option value="AIナレーション">AIナレーション</option>
                        <option value="ロケーション録音">ロケーション録音</option>
                        <option value="その他">その他</option>
                      </select>
                      <i className="ri-arrow-down-s-line absolute right-2 top-1/2 transform -translate-y-1/2 text-zinc-500"></i>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="contact-timeline" className={labelClass}>希望納期</label>
                    <input
                      id="contact-timeline"
                      type="text"
                      name="timeline"
                      value={formData.timeline}
                      onChange={handleInputChange}
                      placeholder="例：1ヶ月以内"
                      className={inputClass}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-budget" className={labelClass}>予算</label>
                  <div className="relative">
                    <select
                      id="contact-budget"
                      name="budget"
                      value={formData.budget}
                      onChange={handleInputChange}
                      className={`${inputClass} pr-8 appearance-none`}
                    >
                      <option value="">選択してください</option>
                      <option value="10万円未満">10万円未満</option>
                      <option value="10万円〜30万円">10万円〜30万円</option>
                      <option value="30万円〜50万円">30万円〜50万円</option>
                      <option value="50万円〜100万円">50万円〜100万円</option>
                      <option value="100万円以上">100万円以上</option>
                      <option value="要相談">要相談</option>
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-2 top-1/2 transform -translate-y-1/2 text-zinc-500"></i>
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-message" className={labelClass}>
                    メッセージ <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="プロジェクトの詳細、ご要望などをお聞かせください"
                    className={`${inputClass} resize-vertical`}
                  ></textarea>
                  <p className="text-xs text-zinc-500 mt-1">{formData.message.length}/500文字</p>
                </div>

                <Button
                  variant="primary"
                  size="lg"
                  disabled={isSubmitting}
                  className="w-full"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line mr-2 animate-spin"></i>
                      送信中...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line mr-2"></i>
                      送信する
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>

          {/* 詳細な流れの説明セクション */}
          <div className="mt-16">
            <h3 className="text-2xl font-mincho text-white mb-10 text-center">
              <i className="ri-bar-chart-horizontal-line text-teal-400 mr-2"></i>
              制作の流れを詳しくご紹介
            </h3>
            <div className="space-y-4">
              {workflowSteps.map((step, index) => {
                const barWidths = [55, 65, 72, 88, 78, 100];
                const width = barWidths[index];
                return (
                  <div key={index} className="flex items-center gap-4">
                    <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-teal-500 text-white rounded-full font-bold text-lg">
                      {step.number}
                    </div>
                    <div className="flex-1">
                      <div
                        className="relative rounded-lg overflow-hidden"
                        style={{ width: `${width}%`, transition: 'width 0.8s ease' }}
                      >
                        <div
                          className="absolute inset-0"
                          style={{
                            background: `linear-gradient(90deg, #0d9488 0%, #14b8a6 60%, #5eead4 100%)`,
                            opacity: 1 - index * 0.08 > 0.4 ? 1 - index * 0.08 : 0.4
                          }}
                        ></div>
                        <div className="relative flex items-center px-5 py-3 gap-3">
                          <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                            <i className={`${step.icon} text-white text-xl`}></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-medium text-base leading-tight whitespace-nowrap">{step.title}</p>
                            <p className="text-white/80 text-xs mt-0.5 whitespace-nowrap">{step.description}</p>
                          </div>
                          <div className="flex-shrink-0 bg-black/20 rounded-full px-2 py-0.5">
                            <span className="text-white text-xs font-semibold">STEP {step.number}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex-shrink-0 w-12 text-right">
                      <span className="text-sm font-bold text-teal-300">{width}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-8 flex items-center justify-center gap-6 text-sm text-zinc-500">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-teal-500"></div>
                <span>各ステップの進行度</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-time-line text-teal-400"></i>
                <span>通常、お問い合わせから納品まで2〜4週間程度</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}